import Bugsnag from '@bugsnag/js'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormHelperText from '@material-ui/core/FormHelperText'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import {
  createStyles,
  makeStyles,
  Theme,
  ThemeProvider,
} from '@material-ui/core/styles'
import { MDCTextField } from '@material/textfield'
import axios, { CancelTokenSource } from 'axios'
import clsx from 'clsx'
import * as cuid from 'cuid'
import React, {
  Fragment,
  Ref,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'

import Button from '../../../../../common/js/Button'
import CurrencyPrice from '../../../../../common/js/CurrencyPrice'
import CartFactory, { IOrderInfo } from '../../../../../common/js/api/cart'
import { calculatePrice } from '../../../../../common/js/api/pricing-calculator'
import UploadArtwork from '../../../../../common/js/upload-artwork/upload-artwork'
import {
  cm2in,
  cm2mm,
  in2cm,
  in2mm,
  mm2in,
} from '../../../../../common/js/util'
import theme from '../../theme'
import { IRegion, parseUnits, Units } from '../RegionChooser/helpers'
import { useRegion } from '../RegionChooser/hook'
import MaterialChooser from './material-chooser'
import MaterialList from './material-list'
import { Material } from './materials'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      '& .MuiFormControlLabel-root': {
        marginLeft: '16px',
      },
      '& .MuiFormControlLabel-label': {
        width: '100%',

        '& span': {
          lineHeight: '42px',
          verticalAlign: 'middle',
        },
      },
      '& .Mui-disabled': {
        backgroundColor: '#CCE4F6',
      },
      '& .MuiRadio-colorSecondary.Mui-disabled': {
        color: '#000026',
      },
      '& .MuiFormControlLabel-label.Mui-disabled': {
        color: '#000026',
      },
    },
    table: {
      border: 'initial',
    },
    margin: {
      margin: theme.spacing(1),
    },
    textField: {
      width: '9ch',
      marginTop: 0,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#000026',
      },
      '& .MuiFormHelperText-contained': {
        marginTop: 0,
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        textAlign: 'center',
        color: '#000026',
      },
      '& .MuiFormHelperText-root': {
        fontSize: 12,
      },
    },
    textFieldInner: {
      padding: theme.spacing(1),
      height: 55,

      '& > input': {
        height: 40,
      },
    },
    error: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'red',
      },
    },
    switchPadding: {
      padding: theme.spacing(1),
    },
  })
)

const sortMaterials = (a: Material, b: Material) => {
  if (a.sortOrder && b.sortOrder) {
    return a.sortOrder - b.sortOrder
  }

  if (a.sortOrder && !b.sortOrder) {
    return -1
  }

  if (!a.sortOrder && b.sortOrder) {
    return 1
  }

  return a.name.localeCompare(b.name)
}

const useHash = () => {
  const [path, setPath] = useState(window.location.hash)
  const listenToPopstate = () => {
    const winPath = window.location.hash
    setPath(winPath)
  }
  useEffect(() => {
    window.addEventListener('popstate', listenToPopstate)
    return () => {
      window.removeEventListener('popstate', listenToPopstate)
    }
  }, [])
  return path
}

interface IProps {
  id: string

  channelId: number
  productId: number

  quantity?: number
  overrideQuantity?: number

  materials: Record<string, Material>
  name: string
}

declare const SI_API_URL: string

const defaultMinWidthCm = 2
const defaultMaxWidthCm = 70

function PricingCalculator(props: IProps) {
  const hash = useHash()
  const classes = useStyles()
  const { t, ready: tReady } = useTranslation('PricingCalculator', {
    useSuspense: false,
  })

  const [cancelToken, setCancelToken] = useState<CancelTokenSource | undefined>(
    null
  )

  const body: HTMLBodyElement = document.querySelector('body')
  const portal: HTMLDivElement = document.querySelector('.si-portal')
  const [showPortal, setShowPortal] = useState(false)

  const { id } = props
  const materials = Object.values(props.materials).sort(sortMaterials)
  const [pricingInfo, setPricingInfo] = useState({} as Record<string, any>)
  const [material, setMaterial] = useState('')
  const [quantity, setQuantity] = useState(props.quantity)
  const [widthIN, setWidthIN] = useState(mm2in(defaultMinWidthCm))
  const [heightIN, setHeightIN] = useState(mm2in(defaultMinWidthCm))
  const [widthMM, setWidthMM] = useState(cm2mm(defaultMinWidthCm))
  const [heightMM, setHeightMM] = useState(cm2mm(defaultMinWidthCm))
  const [widthCM, setWidthCM] = useState(defaultMinWidthCm)
  const [heightCM, setHeightCM] = useState(defaultMinWidthCm)
  const [widthError, setWidthError] = useState(false)
  const [heightError, setHeightError] = useState(false)
  const [quantityError, setQuantityError] = useState(false)
  const [generalError, setGeneralError] = useState(false)
  const [customQuantityValue, setCustomQuantityValue] = useState('')
  const [showMaterialChooser, setShowMaterialChooser] = useState(false)
  const [userChooserMaterial, setUserChooserMaterial] = useState('')
  const [usesFixedMaterial, setUsesFixedMaterial] = useState(false)
  const [allMaterials, setAllMaterials] = useState<Record<string, Material>>({})
  const [minStickerSizeCm, setMinStickerSizeCm] = useState(defaultMinWidthCm)
  const [maxStickerSizeCm, setMaxStickerSizeCm] = useState(defaultMaxWidthCm)
  const [cartId, setCartId] = useState<string>(null)
  const [materialsReady, setMaterialsReady] = useState(false)
  const [pricesReady, setPricesReady] = useState(false)
  const [showCustomQuantity, setShowCustomQuantity] = useState(false)

  const quantityRef: Ref<HTMLLabelElement> = useRef()
  const moqError = quantity < pricingInfo.moq
  const anyError =
    quantityError || widthError || heightError || generalError || moqError

  const {
    ready: rReady,
    units: defaultUnits,
    currencyCode,
    shipToCountryCode,
    pricesIncludeVAT,
    cartPath,
    languageCode,
  } = useRegion()

  const [overrideUnits, setOverrideUnits] = useState<Units>(null)
  const units = overrideUnits || defaultUnits
  const theCart = CartFactory()

  useEffect(() => {
    setMaterial(materials[0].id)
    setUserChooserMaterial(materials[0].id)

    if (props.overrideQuantity && props.overrideQuantity === 0) {
      setQuantity(1)
    } else if (props.overrideQuantity && props.overrideQuantity > 0) {
      setQuantity(props.overrideQuantity)
    }

    theCart.getId().then(setCartId)
  }, [])

  useEffect(() => {
    if (!rReady) {
      return
    }

    if (defaultUnits === 'in') {
      const defaultWidth = 2
      const defaultHeight = 2
      setWidthIN(defaultWidth)
      setWidthMM(in2mm(defaultWidth))
      setWidthCM(in2cm(defaultWidth))
      setHeightIN(defaultHeight)
      setHeightMM(in2mm(defaultHeight))
      setHeightCM(in2cm(defaultHeight))
    }
  }, [rReady])

  useEffect(() => {
    if (!rReady) {
      return
    }

    setWidthError(
      widthMM < cm2mm(minStickerSizeCm) || widthMM > cm2mm(maxStickerSizeCm)
    )
    setHeightError(
      heightMM < cm2mm(minStickerSizeCm) || heightMM > cm2mm(maxStickerSizeCm)
    )

    if (
      widthError ||
      heightError ||
      material === '' ||
      isNaN(quantity) ||
      quantity < 1
    ) {
      return
    }

    if (cancelToken) {
      cancelToken.cancel()
    }

    const newCancelToken = axios.CancelToken.source()
    setCancelToken(newCancelToken)

    calculatePrice(
      props.productId,
      widthMM,
      heightMM,
      quantity,
      material,
      currencyCode,
      shipToCountryCode,
      languageCode,
      newCancelToken.token
    )
      .then((pricingInfo) => setPricingInfo(pricingInfo))
      .then(() => {
        setPricesReady(true)
        setWidthError(false)
        setHeightError(false)
        setQuantityError(false)
        setGeneralError(false)
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          err.response.data.forEach((code: string) => {
            switch (code) {
              case 'IncorrectWidth':
                setWidthError(true)
                break
              case 'IncorrectHeight':
                setHeightError(true)
                break
              case 'QuantityTooSmall':
                setQuantityError(true)
                break
              case 'NoMatchingPricingInfo':
                setGeneralError(true)
                break
              default:
                Bugsnag.notify(err)
                break
            }
          })
        }
      })
  }, [
    rReady,
    currencyCode,
    shipToCountryCode,
    widthMM,
    heightMM,
    quantity,
    material,
    minStickerSizeCm,
    maxStickerSizeCm,
  ])

  useEffect(() => {
    if (!showCustomQuantity || !quantityRef.current) {
      return
    }

    new MDCTextField(quantityRef.current)
  }, [showCustomQuantity])

  useEffect(() => {
    axios
      .get<Record<string, Material>>(`${SI_API_URL}/materials`)
      .then((resp) => {
        setAllMaterials(resp.data)

        const firstMaterial = resp.data[materials[0].id]

        onWidthChange(firstMaterial.minStickerSizeCm)
        onHeightChange(firstMaterial.minStickerSizeCm)

        setQuantity(firstMaterial.moq)

        setMaterialsReady(true)
      })
  }, [])

  useEffect(() => {
    const hashFixedMaterial = hash.startsWith('#pc-')
      ? hash.replace('#pc-', '')
      : ''

    if (hashFixedMaterial.length > 0) {
      setMaterial(hashFixedMaterial)
      setUserChooserMaterial(hashFixedMaterial)
      window.scrollTo({ top: 0 })
    }
  }, [hash])

  useEffect(() => {
    let min = defaultMinWidthCm
    let max = defaultMaxWidthCm

    if (Object.keys(allMaterials).length > 0 && userChooserMaterial !== '') {
      const foundMaterial = allMaterials[userChooserMaterial]
      min = foundMaterial.minStickerSizeCm
      max = foundMaterial.maxStickerSizeCm

      setMinStickerSizeCm(min)
      setMaxStickerSizeCm(max)

      setWidthError(widthMM < cm2mm(min) || widthMM > cm2mm(max))
      setHeightError(heightMM < cm2mm(min) || heightMM > cm2mm(max))
    }
  }, [materials, units, userChooserMaterial])

  useEffect(() => {
    if (showPortal) {
      body.classList.add('si-no-overflow')
      body.parentElement.classList.add('si-no-overflow')
      portal.classList.add('si-visible')
      window.scrollTo({ top: 0 })
    } else {
      body.classList.remove('si-no-overflow')
      body.parentElement.classList.remove('si-no-overflow')
      portal.classList.remove('si-visible')
    }
  }, [showPortal])

  const maybeSetQuantity = (evt: Event, newVal: number) => {
    const asStr = newVal.toString()
    let refocus = false

    if (newVal < 0) {
      newVal = 1
      refocus = true
    } else if (
      asStr.includes('.') &&
      asStr.split('.').reverse()[0].length > 2
    ) {
      newVal = parseFloat(newVal.toFixed(2))
      refocus = true
    }

    if (refocus) {
      ;(evt.target as HTMLInputElement).blur()
      ;(evt.target as HTMLInputElement).focus()
    }

    setQuantity(newVal)
  }

  const onWidthChange = (newVal: number) => {
    if (units === 'cm') {
      setWidthIN(cm2in(+newVal))
      setWidthMM(cm2mm(+newVal))
      setWidthCM(+newVal)

      setWidthError(+newVal < minStickerSizeCm || +newVal > maxStickerSizeCm)
    } else {
      setWidthIN(+newVal)
      setWidthMM(in2mm(+newVal))
      setWidthCM(in2cm(+newVal))

      setWidthError(
        in2cm(+newVal) < minStickerSizeCm || in2cm(+newVal) > maxStickerSizeCm
      )
    }
  }

  const onHeightChange = (newVal: number) => {
    if (units === 'cm') {
      setHeightIN(cm2in(+newVal))
      setHeightMM(cm2mm(+newVal))
      setHeightCM(+newVal)

      setHeightError(+newVal < minStickerSizeCm || +newVal > maxStickerSizeCm)
    } else {
      setHeightIN(+newVal)
      setHeightMM(in2mm(+newVal))
      setHeightCM(in2cm(+newVal))

      setHeightError(
        in2cm(+newVal) < minStickerSizeCm || in2cm(+newVal) > maxStickerSizeCm
      )
    }
  }

  if (!tReady || !rReady) {
    return null
  }

  const sections = []

  sections.push((idx: number) => (
    <>
      <div>
        <div className="si-icon-circle">{idx}</div>
        <h5>{t('enter-size')}</h5>
        <br />
        <h6>
          {t('enter-size-desc', {
            min:
              units === 'cm'
                ? minStickerSizeCm
                : cm2in(minStickerSizeCm).toFixed(2),
            max:
              units === 'cm'
                ? maxStickerSizeCm
                : cm2in(maxStickerSizeCm).toFixed(2),
            units,
          })}
        </h6>
      </div>
      <div className="si-row">
        <div className="si-col si-col-measurement">
          <FormControl
            className={clsx(classes.margin, classes.textField)}
            variant="outlined"
          >
            <OutlinedInput
              required
              className={clsx(classes.textFieldInner, {
                [classes.error]: widthError,
              })}
              type="number"
              inputMode="decimal"
              data-pricingcalculator-element={'width'}
              id="width"
              value={units === 'cm' ? widthCM : widthIN}
              onChange={(evt) => {
                onWidthChange(+(evt.target as HTMLInputElement).value)
              }}
              aria-describedby="outlined-width-helper-text"
              inputProps={{
                'aria-label': 'width',
                min:
                  units === 'cm' ? minStickerSizeCm : cm2in(minStickerSizeCm),
                max:
                  units === 'cm' ? maxStickerSizeCm : cm2in(maxStickerSizeCm),
                step: units === 'cm' ? 0.1 : 0.01,
              }}
              labelWidth={0}
            />
            <FormHelperText id="outlined-width-helper-text">
              {t('width')} ({t(units)})
            </FormHelperText>
          </FormControl>
          {widthError && (
            <p className="si-padded-left si-text-caption si-text-color-validateFail">
              {t('size-error')}
            </p>
          )}
        </div>
        <div className="si-col si-col-measurement">
          <FormControl
            className={clsx(classes.margin, classes.textField)}
            variant="outlined"
          >
            <OutlinedInput
              required
              className={clsx(classes.textFieldInner, {
                [classes.error]: heightError,
              })}
              type="number"
              inputMode="decimal"
              data-pricingcalculator-element={'height'}
              id="height"
              value={units === 'cm' ? heightCM : heightIN}
              onChange={(evt) => {
                onHeightChange(+(evt.target as HTMLInputElement).value)
              }}
              aria-describedby="outlined-height-helper-text"
              inputProps={{
                'aria-label': 'height',
                min:
                  units === 'cm' ? minStickerSizeCm : cm2in(minStickerSizeCm),
                max:
                  units === 'cm' ? maxStickerSizeCm : cm2in(maxStickerSizeCm),
                step: units === 'cm' ? 0.1 : 0.01,
              }}
              labelWidth={0}
            />
            <FormHelperText id="outlined-height-helper-text">
              {t('height')} ({t(units)})
            </FormHelperText>
          </FormControl>
          {heightError && (
            <p className="si-padded-left si-text-caption si-text-color-validateFail">
              {t('size-error')}
            </p>
          )}
        </div>
        <div className="si-col si-text-centered">
          <div className="si-cm-in-switch">
            <span className={classes.switchPadding}>{t('cm')}</span>
            <div
              className={clsx('mdc-switch', {
                'mdc-switch--checked': units === 'in',
              })}
            >
              <div className="mdc-switch__track"></div>
              <div className="mdc-switch__thumb-underlay">
                <div className="mdc-switch__thumb"></div>
                <input
                  data-pricingcalculator-element={'units-switch'}
                  type="checkbox"
                  id="another-basic-switch"
                  className="mdc-switch__native-control"
                  role="switch"
                  aria-checked={units === 'in'}
                  checked={units === 'in'}
                  onChange={(event) => {
                    const isChecked = (event.target as HTMLInputElement).checked
                    const convertTo = isChecked ? 'in' : 'cm'

                    if (convertTo === 'cm') {
                      setWidthMM(+in2mm(widthIN).toFixed(0))
                      setHeightMM(+in2mm(heightIN).toFixed(0))
                      setWidthCM(+in2cm(widthIN).toFixed(2))
                      setHeightCM(+in2cm(heightIN).toFixed(2))
                    } else {
                      setWidthIN(+mm2in(widthMM).toFixed(2))
                      setHeightIN(+mm2in(heightMM).toFixed(2))
                    }

                    setOverrideUnits(convertTo)
                  }}
                />
              </div>
            </div>
            <span className={classes.switchPadding}>{t('in')}</span>
          </div>
        </div>
      </div>
    </>
  ))

  if (
    !usesFixedMaterial &&
    Object.keys(materials).length > 1 &&
    materialsReady
  ) {
    sections.push((idx: number) => (
      <>
        <div className="">
          <div className="si-icon-circle">{idx}</div>
          <h5>{t('select-material')}</h5>
        </div>

        <div className="si-row si-padded-left-sm">
          <Suspense fallback={<div></div>}>
            <MaterialChooser
              allMaterials={allMaterials}
              chosenMaterial={material}
              onClick={() => {
                setShowMaterialChooser(true)
              }}
            />
          </Suspense>
        </div>
      </>
    ))
  }

  if (!props.overrideQuantity && pricesReady) {
    sections.push((idx: number) => (
      <>
        <div className="">
          <div className="si-icon-circle">{idx}</div>
          <h5>{t('choose-quantity')}</h5>
          <h6>
            {t('minimum-quantity', {
              width: units === 'cm' ? widthCM : widthIN.toFixed(2),
              height: units === 'cm' ? heightCM : heightIN.toFixed(2),
              units,
              moq: pricingInfo.moq,
            })}
          </h6>
        </div>

        <div className="si-row">
          <div className="si-col">
            <RadioGroup
              data-pricingcalculator-element={'quantity'}
              className={classes.formControl}
              aria-label="quantity"
              name="quantity1"
              value={!showCustomQuantity && quantity}
              onChange={(evt) => {
                const value = (evt.target as HTMLInputElement).value
                setQuantity(parseInt(value, 10))
                setShowCustomQuantity(false)
                setCustomQuantityValue('')
              }}
            >
              {pricingInfo.quantities &&
                pricingInfo.quantities.map((p: Record<string, any>) => {
                  const checked = !showCustomQuantity && quantity === p.quantity
                  const key = id + 'qty' + p.quantity + p.price

                  return (
                    <FormControlLabel
                      key={key}
                      name={'quantity'}
                      style={{ maxHeight: '42px' }}
                      value={p.quantity}
                      disabled={p.quantity === quantity && !showCustomQuantity}
                      control={
                        <Radio
                          data-pricingcalculator-element={'quantity-radio'}
                        />
                      }
                      label={
                        <div style={{ display: 'flex', width: '100%' }}>
                          <span style={{ width: '25%' }}>{p.quantity}</span>
                          <CurrencyPrice
                            style={{ width: '35%' }}
                            className="si-text-left"
                            currencyCode={currencyCode}
                            price={p.price}
                          />
                          <span
                            className={clsx({
                              'si-discount': !checked,
                              'si-text-caption': checked,
                              'si-text-color-grey': checked,
                            })}
                          >
                            {checked &&
                              t(
                                pricesIncludeVAT
                                  ? 'prices-include-vat'
                                  : 'prices-exclude-vat'
                              )}
                            {!checked &&
                              t('save') +
                                ' ' +
                                (p.totalDiscount * 100).toFixed(0) +
                                '%'}
                          </span>
                        </div>
                      }
                    />
                  )
                })}
            </RadioGroup>

            <RadioGroup
              className={classes.formControl}
              aria-label="custom quantity"
              name="custom-quantity"
              value={customQuantityValue}
              onChange={(evt) => {
                setShowCustomQuantity(true)
                setCustomQuantityValue('custom-quantity')
                // setQuantity(-1)
              }}
            >
              <FormControlLabel
                key={'custom-quantity'}
                value={'custom-quantity'}
                control={
                  <Radio
                    data-pricingcalculator-element={'custom-quantity-radio'}
                  />
                }
                disabled={showCustomQuantity}
                label={<span>{t('custom-quantity')}</span>}
              />
            </RadioGroup>

            {showCustomQuantity && (
              <table className={clsx('si-margin-top-xs', classes.table)}>
                <tr className="si-custom-qty-row" key="custom-quantity-row">
                  <td>
                    <div className="si-padded-left">
                      <label
                        ref={quantityRef}
                        className={clsx(
                          'mdc-text-field mdc-text-field--outlined',
                          {
                            'mdc-text-field--error':
                              quantityError || generalError || moqError,
                          }
                        )}
                      >
                        <input
                          data-pricingcalculator-element={
                            'custom-quantity-value'
                          }
                          required
                          name="qty"
                          type="number"
                          min="1"
                          step="1"
                          className="mdc-text-field__input"
                          aria-labelledby="custom-qty-<?php echo $idx; ?>"
                          placeholder="Quantity"
                          value={quantity}
                          onKeyDown={(evt) => {
                            maybeSetQuantity(
                              evt as any,
                              +(evt.target as HTMLInputElement).value
                            )
                          }}
                          onChange={(evt) => {
                            maybeSetQuantity(
                              evt as any,
                              +(evt.target as HTMLInputElement).value
                            )
                          }}
                        />
                        <span className="mdc-notched-outline"></span>
                      </label>
                    </div>
                  </td>
                  <td width="25%" className="si-text-centered">
                    {!anyError && pricingInfo && pricingInfo.price && (
                      <>
                        <CurrencyPrice
                          className="si-price"
                          price={pricingInfo.price.price}
                          currencyCode={currencyCode}
                        />
                      </>
                    )}
                  </td>
                </tr>
              </table>
            )}

            {showCustomQuantity &&
              (quantityError || generalError || moqError) && (
                <p className="si-padded-left si-text-caption si-text-color-validateFail">
                  Quantity is insufficient for sticker size
                </p>
              )}
          </div>
        </div>
      </>
    ))
  }

  const orderInfo: IOrderInfo = {
    productId: pricingInfo.productId,
    pricingQuoteId: pricingInfo.pricingQuoteId,
    materialId: material,
    quantity: props.overrideQuantity ?? quantity,
    widthInUnit: units === 'cm' ? widthCM : widthIN,
    heightInUnit: units === 'cm' ? heightCM : heightIN,
    widthMM,
    heightMM,
    units,
    currency: pricingInfo.currency,
    instructions: '',
  }

  const myPortal = showPortal
    ? ReactDOM.createPortal(
        <UploadArtwork
          pricingInfo={pricingInfo}
          onClose={() => {
            setShowPortal(false)
          }}
          onSubmit={(instructions, files) => {
            theCart
              .addToCart(
                { ...orderInfo, instructions },
                pricingInfo,
                files.map((f) => f.uploadedId),
                cartId,
                props.channelId,
                props.name
              )
              .then(() => {
                window.location.href = cartPath
              })
              .catch((err: any) => {
                Bugsnag.notify(err)
              })
          }}
        />,
        portal
      )
    : null

  if (showMaterialChooser) {
    return (
      <Suspense fallback={<div></div>}>
        <MaterialList
          materials={materials}
          defaultMaterial={userChooserMaterial}
          onCancel={() => {
            setShowMaterialChooser(false)
          }}
          onApply={(chosenMaterial) => {
            setShowMaterialChooser(false)
            setMaterial(chosenMaterial)
            setUserChooserMaterial(chosenMaterial)
          }}
        />
      </Suspense>
    )
  }

  return (
    <>
      {myPortal}
      <div id="pc"></div>
      <div className="si-padded">
        {sections.map((section, idx: number) => (
          <div key={idx} className="si-pricing-calculator-section">
            {section(idx + 1)}
          </div>
        ))}
      </div>

      <div className="si-padded si-background-color-greenLight si-text-centered">
        <Button
          data-pricingcalculator-element={'continue-button'}
          disabled={anyError}
          onClick={() => {
            setShowPortal(true)
          }}
          color="coolBlueDark"
          fullWidth={true}
        >
          {t('continue')}{' '}
          {!anyError && pricingInfo && pricingInfo.price && (
            <CurrencyPrice
              price={pricingInfo.price.price}
              currencyCode={currencyCode}
            />
          )}
        </Button>
        <p className="si-text-caption si-text-right si-padded-top">
          {t('continue-text')}
        </p>
      </div>
    </>
  )
}

export default (element: Element) => {
  const defaultQuantityEl: HTMLInputElement = element.querySelector(
    "input[name='defaultQuantity']"
  )
  const overrideQuantityEl: HTMLInputElement = element.querySelector(
    "input[name='overrideQuantity']"
  )

  const channelId: HTMLInputElement = element.querySelector(
    "input[name='channelId']"
  )
  const productId: HTMLInputElement = element.querySelector(
    "input[name='productId']"
  )
  const name: HTMLInputElement = element.querySelector("input[name='name']")
  const availableMaterialsInput: HTMLInputElement = element.querySelector(
    "input[name='materials']"
  )
  const decoded = atob(availableMaterialsInput.value)
  const parsed = JSON.parse(decoded)
  const availableMaterials: Record<string, Material> =
    parsed.length > 0 ? parsed : []

  try {
    ReactDOM.render(
      <ThemeProvider theme={theme}>
        <PricingCalculator
          id={cuid()}
          channelId={+channelId.value}
          productId={+productId.value}
          quantity={+defaultQuantityEl?.value}
          materials={availableMaterials}
          overrideQuantity={
            overrideQuantityEl ? +overrideQuantityEl?.value : null
          }
          name={name.value}
        />
      </ThemeProvider>,
      element
    )
  } catch (err) {
    Bugsnag.notify(err)
    console.error(err)
  }
}
